/**
 * Redis Cluster Configuration and Management
 * High-performance caching layer with clustering support
 */

import { createCluster, RedisClusterType } from 'redis';
import { logger } from '@/lib/logger';

interface RedisClusterConfig {
  nodes: Array<{ host: string; port: number }>;
  options: {
    enableReadyCheck: boolean;
    redisOptions: {
      password?: string;
      connectTimeout: number;
      lazyConnect: boolean;
    };
    maxRetriesPerRequest: number;
    retryDelayOnFailover: number;
    enableOfflineQueue: boolean;
    readOnly: boolean;
    scaleReads: 'master' | 'slave' | 'all';
  };
}

interface CacheItem<T = any> {
  value: T;
  timestamp: number;
  ttl?: number;
  tags?: string[];
}

interface CacheStats {
  hits: number;
  misses: number;
  hitRate: number;
  totalRequests: number;
  avgResponseTime: number;
  memoryUsage: string;
}

export class RedisCluster {
  private static instance: RedisCluster;
  private cluster: RedisClusterType | null = null;
  private readOnlyCluster: RedisClusterType | null = null;
  private config: RedisClusterConfig;
  private initialized = false;
  private stats: CacheStats = {
    hits: 0,
    misses: 0,
    hitRate: 0,
    totalRequests: 0,
    avgResponseTime: 0,
    memoryUsage: '0B',
  };

  private constructor() {
    this.config = this.buildClusterConfig();
  }

  public static getInstance(): RedisCluster {
    if (!RedisCluster.instance) {
      RedisCluster.instance = new RedisCluster();
    }
    return RedisCluster.instance;
  }

  /**
   * Build Redis cluster configuration
   */
  private buildClusterConfig(): RedisClusterConfig {
    const isProduction = process.env.NODE_ENV === 'production';
    const redisPassword = process.env.REDIS_PASSWORD;
    
    // Parse Redis cluster nodes from environment
    const nodesString = process.env.REDIS_CLUSTER_NODES || 'localhost:6379';
    const nodes = nodesString.split(',').map(node => {
      const [host, portStr] = node.trim().split(':');
      return {
        host: host || 'localhost',
        port: parseInt(portStr) || 6379,
      };
    });

    return {
      nodes,
      options: {
        enableReadyCheck: true,
        redisOptions: {
          ...(redisPassword && { password: redisPassword }),
          connectTimeout: 10000,
          lazyConnect: true,
        },
        maxRetriesPerRequest: 3,
        retryDelayOnFailover: 100,
        enableOfflineQueue: false,
        readOnly: false,
        scaleReads: isProduction ? 'slave' : 'master',
      },
    };
  }

  /**
   * Initialize Redis cluster connections
   */
  async initialize(): Promise<boolean> {
    if (this.initialized) {
      return true;
    }

    // Skip Redis initialization if no Redis URL is configured
    if (!process.env.REDIS_URL && !process.env.REDIS_CLUSTER_NODES) {
      logger.info('Redis not configured, skipping cluster initialization');
      this.initialized = true; // Mark as initialized to prevent repeated attempts
      return true;
    }

    try {
      logger.info('Initializing Redis cluster...', {
        nodes: this.config.nodes,
        scaleReads: this.config.options.scaleReads,
      });

      // Create write cluster
      this.cluster = createCluster({
        rootNodes: this.config.nodes,
        defaults: {
          ...this.config.options.redisOptions,
        },
      });

      // Create read-only cluster for read operations
      if (this.config.options.scaleReads === 'slave') {
        this.readOnlyCluster = createCluster({
          rootNodes: this.config.nodes,
          defaults: {
            ...this.config.options.redisOptions,
          },
          useReplicas: true,
        });
      }

      // Setup error handlers
      this.setupEventHandlers();

      // Connect to cluster with timeout
      const connectTimeout = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Redis cluster connection timeout')), 10000);
      });

      await Promise.race([
        this.cluster.connect(),
        connectTimeout
      ]);
      
      if (this.readOnlyCluster) {
        await Promise.race([
          this.readOnlyCluster.connect(),
          connectTimeout
        ]);
      }

      // Test connection
      await this.cluster.ping();
      
      this.initialized = true;
      logger.info('Redis cluster initialized successfully');

      // Start periodic stats collection
      this.startStatsCollection();

      return true;
    } catch (error) {
      logger.warn('Redis cluster initialization failed, continuing without Redis', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      
      // Mark as initialized to prevent repeated attempts
      this.initialized = true;
      return false;
    }
  }

  /**
   * Setup cluster event handlers
   */
  private setupEventHandlers(): void {
    if (!this.cluster) return;

    this.cluster.on('error', (error) => {
      logger.error('Redis cluster error', { error: error.message });
    });

    this.cluster.on('connect', () => {
      logger.info('Redis cluster connected');
    });

    this.cluster.on('ready', () => {
      logger.info('Redis cluster ready');
    });

    this.cluster.on('close', () => {
      logger.warn('Redis cluster connection closed');
    });

    this.cluster.on('reconnecting', () => {
      logger.info('Redis cluster reconnecting...');
    });

    this.cluster.on('end', () => {
      logger.warn('Redis cluster connection ended');
    });
  }

  /**
   * Get value from cache
   */
  async get<T>(key: string): Promise<T | null> {
    if (!this.cluster) {
      return null; // Return null instead of throwing error when Redis is not available
    }

    const startTime = Date.now();
    
    try {
      const client = this.readOnlyCluster || this.cluster;
      const raw = await client.get(key);
      
      if (raw) {
        const cached: CacheItem<T> = JSON.parse(raw);
        
        // Check TTL
        if (cached.ttl && Date.now() - cached.timestamp > cached.ttl * 1000) {
          await this.delete(key);
          this.recordMiss(Date.now() - startTime);
          return null;
        }
        
        this.recordHit(Date.now() - startTime);
        return cached.value;
      }
      
      this.recordMiss(Date.now() - startTime);
      return null;
    } catch (error) {
      logger.error('Redis get error', {
        key,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      this.recordMiss(Date.now() - startTime);
      return null;
    }
  }

  /**
   * Set value in cache
   */
  async set<T>(key: string, value: T, ttlSeconds?: number, tags?: string[]): Promise<boolean> {
    if (!this.cluster) {
      return false; // Return false instead of throwing error when Redis is not available
    }

    try {
      const cacheItem: CacheItem<T> = {
        value,
        timestamp: Date.now(),
        ...(ttlSeconds && { ttl: ttlSeconds }),
        ...(tags && { tags }),
      };

      const serialized = JSON.stringify(cacheItem);
      
      if (ttlSeconds) {
        await this.cluster.setEx(key, ttlSeconds, serialized);
      } else {
        await this.cluster.set(key, serialized);
      }

      // Store tags for invalidation
      if (tags && tags.length > 0) {
        for (const tag of tags) {
          await this.cluster.sAdd(`tag:${tag}`, key);
          if (ttlSeconds) {
            await this.cluster.expire(`tag:${tag}`, ttlSeconds);
          }
        }
      }

      return true;
    } catch (error) {
      logger.error('Redis set error', {
        key,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return false;
    }
  }

  /**
   * Delete key from cache
   */
  async delete(key: string): Promise<boolean> {
    if (!this.cluster) {
      return false; // Return false instead of throwing error when Redis is not available
    }

    try {
      const deleted = await this.cluster.del(key);
      return deleted > 0;
    } catch (error) {
      logger.error('Redis delete error', {
        key,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return false;
    }
  }

  /**
   * Delete multiple keys
   */
  async deleteMany(keys: string[]): Promise<number> {
    if (!this.cluster || keys.length === 0) {
      return 0;
    }

    try {
      return await this.cluster.del(keys);
    } catch (error) {
      logger.error('Redis deleteMany error', {
        keysCount: keys.length,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return 0;
    }
  }

  /**
   * Check if key exists
   */
  async exists(key: string): Promise<boolean> {
    if (!this.cluster) {
      return false;
    }

    try {
      const exists = await this.cluster.exists(key);
      return exists > 0;
    } catch (error) {
      logger.error('Redis exists error', {
        key,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return false;
    }
  }

  /**
   * Invalidate cache by tags
   */
  async invalidateByTags(tags: string[]): Promise<number> {
    if (!this.cluster || tags.length === 0) {
      return 0;
    }

    try {
      let totalDeleted = 0;
      
      for (const tag of tags) {
        const tagKey = `tag:${tag}`;
        const keys = await this.cluster.sMembers(tagKey);
        
        if (keys.length > 0) {
          const deleted = await this.deleteMany(keys);
          totalDeleted += deleted;
        }
        
        // Clean up the tag set
        await this.cluster.del(tagKey);
      }
      
      logger.info('Cache invalidated by tags', {
        tags,
        keysDeleted: totalDeleted,
      });
      
      return totalDeleted;
    } catch (error) {
      logger.error('Redis invalidateByTags error', {
        tags,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return 0;
    }
  }

  /**
   * Get or set pattern (cache-aside)
   */
  async getOrSet<T>(
    key: string,
    fetcher: () => Promise<T>,
    ttlSeconds?: number,
    tags?: string[]
  ): Promise<T> {
    // Try to get from cache first
    const cached = await this.get<T>(key);
    if (cached !== null) {
      return cached;
    }

    // Fetch from source
    const value = await fetcher();
    
    // Store in cache
    await this.set(key, value, ttlSeconds, tags);
    
    return value;
  }

  /**
   * Increment counter
   */
  async increment(key: string, increment = 1): Promise<number> {
    if (!this.cluster) {
      return 0; // Return 0 instead of throwing error when Redis is not available
    }

    try {
      return await this.cluster.incrBy(key, increment);
    } catch (error) {
      logger.error('Redis increment error', {
        key,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  /**
   * Set expiration for key
   */
  async expire(key: string, seconds: number): Promise<boolean> {
    if (!this.cluster) {
      return false;
    }

    try {
      return await this.cluster.expire(key, seconds);
    } catch (error) {
      logger.error('Redis expire error', {
        key,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return false;
    }
  }

  /**
   * Get cluster info
   */
  async getClusterInfo(): Promise<any> {
    if (!this.cluster) {
      return null;
    }

    try {
      return await this.cluster.clusterInfo();
    } catch (error) {
      logger.error('Redis cluster info error', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return null;
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    return { ...this.stats };
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.stats = {
      hits: 0,
      misses: 0,
      hitRate: 0,
      totalRequests: 0,
      avgResponseTime: 0,
      memoryUsage: '0B',
    };
  }

  /**
   * Record cache hit
   */
  private recordHit(responseTime: number): void {
    this.stats.hits++;
    this.stats.totalRequests++;
    this.updateStats(responseTime);
  }

  /**
   * Record cache miss
   */
  private recordMiss(responseTime: number): void {
    this.stats.misses++;
    this.stats.totalRequests++;
    this.updateStats(responseTime);
  }

  /**
   * Update statistics
   */
  private updateStats(responseTime: number): void {
    this.stats.hitRate = this.stats.totalRequests > 0 
      ? (this.stats.hits / this.stats.totalRequests) * 100 
      : 0;
    
    this.stats.avgResponseTime = this.stats.totalRequests > 0
      ? (this.stats.avgResponseTime * (this.stats.totalRequests - 1) + responseTime) / this.stats.totalRequests
      : responseTime;
  }

  /**
   * Start periodic stats collection
   */
  private startStatsCollection(): void {
    setInterval(async () => {
      try {
        if (this.cluster) {
          const info = await this.cluster.memory('usage', 'total');
          this.stats.memoryUsage = this.formatBytes(info || 0);
        }
      } catch (error) {
        // Ignore stats collection errors
      }
    }, 30000); // Every 30 seconds
  }

  /**
   * Format bytes to human readable string
   */
  private formatBytes(bytes: number): string {
    const sizes = ['B', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100}${sizes[i]}`;
  }

  /**
   * Flush all cache data
   */
  async flushAll(): Promise<boolean> {
    if (!this.cluster) {
      return false;
    }

    try {
      await this.cluster.flushAll();
      logger.warn('Redis cache flushed');
      return true;
    } catch (error) {
      logger.error('Redis flush error', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return false;
    }
  }

  /**
   * Disconnect from cluster
   */
  async disconnect(): Promise<void> {
    try {
      if (this.cluster) {
        await this.cluster.disconnect();
      }
      if (this.readOnlyCluster) {
        await this.readOnlyCluster.disconnect();
      }
      
      this.initialized = false;
      logger.info('Redis cluster disconnected');
    } catch (error) {
      logger.error('Redis disconnect error', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ healthy: boolean; latency: number; error?: string }> {
    if (!this.cluster) {
      return { healthy: false, latency: 0, error: 'Not initialized' };
    }

    const startTime = Date.now();
    
    try {
      await this.cluster.ping();
      const latency = Date.now() - startTime;
      
      return { healthy: true, latency };
    } catch (error) {
      const latency = Date.now() - startTime;
      
      return {
        healthy: false,
        latency,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}

// Export singleton instance
export const redisCluster = RedisCluster.getInstance();